import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, PackagePlus } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { OmCard, OmHead, OmKpi, OmTable, Row, Td, Pill, GOLD, MUTED, NAVY, RED, GREEN, FIELD } from "@/components/om/OmBits";

const money = (v) => `₹${Number(v || 0).toLocaleString("en-IN")}`;
const BLANK = { name: "", part_no: "", unit: "Nos", stock: 0, min_stock: 0, supplier: "", lead_time_days: 7, unit_cost: 0 };

export default function OmInventory() {
  const [rows, setRows] = useState([]);
  const [log, setLog] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(BLANK);
  const [top, setTop] = useState(null);
  const [rec, setRec] = useState({ qty: 0, supplier: "", invoice_no: "", cost: 0 });

  const load = () => {
    api.get("/om/spares").then(({ data }) => setRows(data));
    api.get("/om/stock-log").then(({ data }) => setLog(data));
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      await api.post("/om/spares", { ...form, stock: Number(form.stock), min_stock: Number(form.min_stock), lead_time_days: Number(form.lead_time_days), unit_cost: Number(form.unit_cost) });
      toast.success("Spare part registered");
      setOpen(false); setForm(BLANK); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const replenish = async () => {
    try {
      await api.post(`/om/spares/${top.id}/replenish`, { ...rec, qty: Number(rec.qty), cost: Number(rec.cost) });
      toast.success(`${rec.qty} ${top.unit} of ${top.name} received into store`);
      setTop(null); setRec({ qty: 0, supplier: "", invoice_no: "", cost: 0 }); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const value = rows.reduce((s, r) => s + r.total_value, 0);
  const low = rows.filter((r) => r.low);

  return (
    <div data-testid="om-inventory-page">
      <OmHead title="Spares & Inventory" subtitle="Stock is deducted automatically when spares are consumed on a PM or CM work order.">
        <Button onClick={() => setOpen(true)} className="font-semibold" style={{ background: GOLD, color: NAVY, borderRadius: 8 }} data-testid="om-spare-add-btn">
          <Plus className="w-4 h-4 mr-1.5" /> Add spare part
        </Button>
      </OmHead>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
        <OmKpi label="Part numbers" value={rows.length} testid="kpi-spares-count" />
        <OmKpi label="Inventory value" value={money(value)} testid="kpi-spares-value" />
        <OmKpi label="Below minimum" value={low.length} tone={low.length ? RED : GREEN} testid="kpi-spares-low" />
        <OmKpi label="Receipts logged" value={log.length} />
      </div>

      <OmCard className="mb-3">
        <OmTable head={["Part", "Part no.", "Unit", "Stock", "Min", "Supplier", "Lead time", "Unit cost", "Value", ""]} testid="om-spares-table">
          {rows.map((r, i) => (
            <Row key={r.id} i={i} testid={`om-spare-row-${r.id}`}>
              <Td>{r.name}</Td>
              <Td style={{ color: MUTED }}>{r.part_no || "—"}</Td>
              <Td style={{ color: MUTED }}>{r.unit}</Td>
              <Td className="tabular-nums">{r.low ? <Pill tone="bad">{r.stock}</Pill> : r.stock}</Td>
              <Td className="tabular-nums" style={{ color: MUTED }}>{r.min_stock}</Td>
              <Td style={{ color: MUTED }}>{r.supplier || "—"}</Td>
              <Td className="tabular-nums" style={{ color: MUTED }}>{r.lead_time_days} d</Td>
              <Td className="tabular-nums">{money(r.unit_cost)}</Td>
              <Td className="tabular-nums">{money(r.total_value)}</Td>
              <Td>
                <button onClick={() => { setTop(r); setRec({ qty: 0, supplier: r.supplier || "", invoice_no: "", cost: 0 }); }} className="text-[11.5px] hover:underline" style={{ color: GOLD }} data-testid={`om-spare-replenish-${r.id}`}>
                  Receive
                </button>
              </Td>
            </Row>
          ))}
          {rows.length === 0 ? <Row i={0}><Td className="text-[12.5px]">No spare parts registered yet.</Td></Row> : null}
        </OmTable>
      </OmCard>

      <OmCard testid="om-stock-log-card">
        <h3 className="text-white font-semibold text-[15px] mb-2">Replenishment log</h3>
        <OmTable head={["Date", "Part", "Qty", "Supplier", "Invoice", "Cost", "By"]}>
          {log.map((l, i) => (
            <Row key={l.id} i={i} testid="om-stock-log-row">
              <Td className="tabular-nums">{new Date(l.at).toLocaleDateString("en-IN")}</Td>
              <Td>{l.name}</Td>
              <Td className="tabular-nums">{l.qty}</Td>
              <Td style={{ color: MUTED }}>{l.supplier || "—"}</Td>
              <Td style={{ color: MUTED }}>{l.invoice_no || "—"}</Td>
              <Td className="tabular-nums">{money(l.cost)}</Td>
              <Td style={{ color: MUTED }}>{l.by}</Td>
            </Row>
          ))}
          {log.length === 0 ? <Row i={0}><Td className="text-[12.5px]">No receipts logged yet.</Td></Row> : null}
        </OmTable>
      </OmCard>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl" style={{ background: "#112240", borderColor: `${GOLD}33` }}>
          <DialogHeader><DialogTitle className="text-white">Add a spare part</DialogTitle></DialogHeader>
          <div className="grid sm:grid-cols-2 gap-3">
            {[["name", "Part name", "text"], ["part_no", "Part number", "text"], ["unit", "Unit", "text"],
              ["stock", "Current stock", "number"], ["min_stock", "Minimum stock level", "number"],
              ["supplier", "Supplier", "text"], ["lead_time_days", "Lead time (days)", "number"],
              ["unit_cost", "Unit cost (₹)", "number"]].map(([k, l, t]) => (
              <div key={k} className="space-y-1.5">
                <Label className="text-[11px] uppercase" style={{ color: MUTED }}>{l}</Label>
                <Input type={t} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} style={FIELD} data-testid={`om-spare-${k}-input`} />
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button onClick={create} disabled={!form.name} className="font-semibold" style={{ background: GOLD, color: NAVY }} data-testid="om-spare-save-btn">Add part</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!top} onOpenChange={(o) => !o && setTop(null)}>
        <DialogContent className="max-w-md" style={{ background: "#112240", borderColor: `${GOLD}33` }}>
          <DialogHeader><DialogTitle className="text-white">Receive stock — {top?.name}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            {[["qty", "Quantity received", "number"], ["cost", "Total cost (₹)", "number"],
              ["supplier", "Supplier", "text"], ["invoice_no", "Invoice number", "text"]].map(([k, l, t]) => (
              <div key={k} className="space-y-1.5">
                <Label className="text-[11px] uppercase" style={{ color: MUTED }}>{l}</Label>
                <Input type={t} value={rec[k]} onChange={(e) => setRec({ ...rec, [k]: e.target.value })} style={FIELD} data-testid={`om-replenish-${k}-input`} />
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button onClick={replenish} disabled={!Number(rec.qty)} className="font-semibold" style={{ background: GOLD, color: NAVY }} data-testid="om-replenish-save-btn">
              <PackagePlus className="w-4 h-4 mr-1.5" /> Add to stock
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
